import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface ProductivityState {
  /** Timestamp (ms) when the timer was first started */
  startedAt: number | null;
  /** Accumulated seconds from previous runs (before latest pause) */
  bankedSeconds: number;
  /** Whether the timer is currently running */
  isRunning: boolean;

  // Actions
  start: () => void;
  pause: () => void;
  toggle: () => void;
  reset: () => void;

  /** Returns total elapsed seconds (banked + current run) */
  getElapsed: () => number;
}

export const useProductivityStore = create<ProductivityState>()(
  persist(
    (set, get) => ({
      startedAt: null,
      bankedSeconds: 0,
      isRunning: false,

      start: () => {
        set({ startedAt: Date.now(), isRunning: true });
      },

      pause: () => {
        const { startedAt, bankedSeconds } = get();
        const runSeconds = startedAt ? (Date.now() - startedAt) / 1000 : 0;
        set({
          bankedSeconds: bankedSeconds + runSeconds,
          startedAt: null,
          isRunning: false,
        });
      },

      toggle: () => {
        const { isRunning, start, pause } = get();
        if (isRunning) pause();
        else start();
      },

      reset: () => {
        set({ startedAt: null, bankedSeconds: 0, isRunning: false });
      },

      getElapsed: () => {
        const { startedAt, bankedSeconds } = get();
        const runSeconds = startedAt ? (Date.now() - startedAt) / 1000 : 0;
        return bankedSeconds + runSeconds;
      },
    }),
    {
      name: 'productivity-timer',
      partialize: (state) => ({
        startedAt: state.startedAt,
        bankedSeconds: state.bankedSeconds,
        isRunning: state.isRunning,
      }),
    }
  )
);
